import type { HTMLAttributes, ReactNode } from 'react';

export type BadgeTone =
  | 'neutral'
  | 'success'
  | 'warn'
  | 'danger'
  | 'info'
  // R-ACTIVITY-V1UI — V1 RecentActivities statusConfig needs two tones the
  // original palette lacked: purple (queued / "Kuyrukta") and orange
  // (refunded / "İade"). CSS: .badge-purple / .badge-orange in styles.css.
  | 'purple'
  | 'orange';

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'badge-neutral',
  success: 'badge-success',
  warn: 'badge-warn',
  danger: 'badge-danger',
  info: 'badge-info',
  purple: 'badge-purple',
  orange: 'badge-orange',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: BadgeTone;
  readonly dot?: boolean;
  readonly children?: ReactNode;
}

export function Badge({ tone = 'neutral', dot = false, className, children, ...rest }: BadgeProps): JSX.Element {
  const classes = ['badge', TONE_CLASS[tone], className ?? ''].filter(Boolean).join(' ');
  return (
    <span className={classes} {...rest}>
      {dot ? <span className="badge-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/**
 * StatusBadge is the same primitive but auto-picks a tone from a
 * known status keyword. Useful for terse table cells.
 */
// Traffic-light status → tone map (V1-parity coloring). Keys are matched
// case-insensitively. Covers the full V2 order-state lifecycle and the
// wallet transaction statuses so the order/transaction tables (recent
// activity, user inspect, order inspect) render in colour instead of a
// flat grey. Every StatusBadge consumer in the app is an order/transaction
// context, so these tones are globally correct (no cross-domain collision).
const STATUS_TONE_MAP: Record<string, BadgeTone> = {
  // --- generic keywords (legacy) ---
  ok: 'success',
  ready: 'success',
  active: 'success',
  success: 'success',
  completed: 'success',
  pending: 'warn',
  waiting: 'warn',
  expiring: 'warn',
  warn: 'warn',
  warning: 'warn',
  fail: 'danger',
  failed: 'danger',
  error: 'danger',
  blocked: 'danger',
  expired: 'danger',
  info: 'info',
  new: 'info',

  // --- V2 order states (green = done, yellow = waiting, blue = in flight,
  //     red = failed/cancelled). Source: packages/contracts OrderState.
  //     R-ACTIVITY-V1UI: reserved/queued/refunded aligned with the V1
  //     statusConfig buckets the activity page uses (reserved folds into
  //     V1 "Aktif" = blue; queued = purple; refunded = orange) so the same
  //     status never renders a different colour on different admin pages. ---
  delivered: 'success',          // verification mail received → completed
  settled: 'success',            // terminal success (post-dispute/finalize)
  pending_reservation: 'warn',   // awaiting a reservation slot
  reserved: 'info',              // alias reserved — V1 bucket "Aktif" (blue)
  assigned: 'info',              // alias assigned, fulfillment in flight
  in_delivery: 'info',           // polling for the verification mail
  cancelled: 'danger',           // customer/lease cancel
  dispute_open: 'danger',        // open dispute (needs attention)
  dispute_resolved: 'warn',      // resolved but flagged for review

  // --- wallet transaction statuses ---
  posted: 'success',             // ledger entry committed
  refunded: 'orange',            // V1 "İade" (orange) — distinct from a sale
  rejected: 'danger',
  reversed: 'warn',
  queued: 'purple',              // V1 wire status "Kuyrukta" (purple)
};

export function StatusBadge({
  status,
  label,
  dot = true,
  ...rest
}: { readonly status: string; readonly label?: ReactNode; readonly dot?: boolean } & Omit<BadgeProps, 'tone' | 'children'>): JSX.Element {
  const tone = STATUS_TONE_MAP[status.toLowerCase()] ?? 'neutral';
  return (
    <Badge tone={tone} dot={dot} {...rest}>
      {label ?? status}
    </Badge>
  );
}
